import { InvoiceList } from './Invoices';
import { PaymentMethod } from './Invoices/paymentMethods';
import { ActivityType } from './activityTypes';
import { BusinessTypeList } from './businessTypes';
import { CallOutcomeList } from './calls';
import { CompanyList } from './companies';
import { ContactList } from './contacts';
import { CustomFieldDefinitionList } from './customFieldDefinitions';
import { DealPhase } from './dealPhases';
import { DealPipelineList } from './dealPipelines';
import { DealList } from './deals';
import { DepartmentList } from './departments';
import { EventList } from './events';
import { LevelTwoAreasList } from './levelTwoAreas';
import { MeetingList } from './meetings';
import { MilestoneList } from './milestones';
import { GroupList } from './nextgen/groups';
import { ProjectList as NextgenProjectList } from './nextgen/projects';
import { TaskList as NextgenTaskList } from './nextgen/tasks';
import { PlannableItemList } from './plannableItems';
import { ProductList } from './products';
import { ProjectList } from './projects';
import { QuotationList } from './quotations';
import { ReservationList } from './reservations';
import { TaskList } from './tasks';
import { TeamList } from './teams';
import { TicketList } from './tickets';
import { UnitOfMeasure } from './unitOfMeasure';
import { UserList } from './users';
import { WorkOrderList } from './workOrders';
import { WorkTypeList } from './workTypes';

export enum Entity {
  ActivityType = 'activityType',
  CreditNote = 'creditNote',
  Department = 'department',
  User = 'user',
  AreaLevelTwo = 'area_level_two',
  Account = 'account',
  Team = 'team',
  CustomFieldDefinition = 'custom_field_definition',
  WorkType = 'work_type',
  Contact = 'contact',
  Company = 'company',
  Customer = 'customer',
  BusinessType = 'businessType',
  Deal = 'deal',
  DealPhase = 'dealPhase',
  DealPipeline = 'dealPipeline',
  Milestone = 'milestone',
  Project = 'project',
  PriceList = 'priceList',
  Product = 'product',
  File = 'file',
  Invoice = 'invoice',
  QuotationEmailTemplate = 'quotation_email_template',
  PaymentMethod = 'paymentMethod',
  ProductCategory = 'productCategory',
  Sector = 'sector',
  CallOutcome = 'callCoutcome',
  Meeting = 'meeting',
  CustomerMeetingRoom = 'customerMeetingRoom',
  Timetracking = 'timetracking',
  NextgenProject = 'nextgenProject',
  NextgenTask = 'nextgenTask',
  NextgenMaterial = 'nextgenMaterial',
  NextgenGroup = 'nextgenProjectGroup',
  NextgenMeeting = 'nextgenMeeting',
  NextgenFilter = 'nextgenProjectFilter',
  Quotation = 'quotation',
  UnitOfMeasure = 'unitOfMeasure',
  WorkOrder = 'workOrder',
  Event = 'event',
  Task = 'task',
  Todo = 'todo',
  PlannableItem = 'plannableItem',
  PlaceholderDocument = 'placeholderDocument',
  DayOffType = 'dayOffType',
  ClosingDay = 'closingDay',
  IncomingInvoice = 'incomingInvoice',
  IncomingCreditNote = 'incomingCreditNote',
  Expense = 'expense',
  Receipt = 'receipt',
  Ticket = 'ticket',
  Call = 'call',
  ExternalEvent = 'externalEvent',
  Reservation = 'reservation',
  Subscription = 'subscription',
}

export interface Reference<T extends Entity> {
  type: T;
  id: string;
  [x: string]: any;
}

export interface TypeEntityMap {
  [Entity.ActivityType]: ActivityType;
  [Entity.Department]: DepartmentList;
  [Entity.User]: UserList;
  [Entity.Team]: TeamList;
  [Entity.CustomFieldDefinition]: CustomFieldDefinitionList;
  [Entity.WorkType]: WorkTypeList;
  [Entity.Contact]: ContactList;
  [Entity.Company]: CompanyList;
  [Entity.BusinessType]: BusinessTypeList;
  [Entity.AreaLevelTwo]: LevelTwoAreasList;
  [Entity.Milestone]: MilestoneList;
  [Entity.Project]: ProjectList;
  [Entity.PaymentMethod]: PaymentMethod;
  [Entity.Invoice]: InvoiceList;
  [Entity.Sector]: InvoiceList;
  [Entity.CallOutcome]: CallOutcomeList;
  [Entity.Deal]: DealList;
  [Entity.DealPipeline]: DealPipelineList;
  [Entity.DealPhase]: DealPhase;
  [Entity.Quotation]: QuotationList;
  [Entity.Product]: ProductList;
  [Entity.UnitOfMeasure]: UnitOfMeasure;
  [Entity.Meeting]: MeetingList;
  [Entity.NextgenProject]: NextgenProjectList;
  [Entity.WorkOrder]: WorkOrderList;
  [Entity.Task]: TaskList;
  [Entity.Todo]: TaskList;
  [Entity.NextgenGroup]: GroupList;
  [Entity.NextgenTask]: NextgenTaskList;
  [Entity.PlannableItem]: PlannableItemList;
  [Entity.Ticket]: TicketList;
  [Entity.Event]: EventList;
  [Entity.Reservation]: ReservationList;
}

/**
 * Creates a type where all given Keys become sideloaded, given that their types are defined in the TypeEntityMap above.
 * Keys can be defined in dot notation and both normal and nullable references are supported.
 */
export type Sideloaded<T, Key extends string> = UnionToIntersection<
  Key extends `${infer Left}.${infer Right}`
    ? T extends { [x in Left]?: unknown }
      ? T & { [key in Left]?: Sideloaded<T[Left], Right> }
      : never
    : T extends { [key in Key]?: Reference<infer R extends keyof TypeEntityMap> | null }
      ? T & {
          [key in Key]?: TypeEntityMap[R] | null;
        }
      : T extends { [key in Key]?: Array<Reference<infer R extends keyof TypeEntityMap>> }
        ? T & {
            [key in Key]?: Array<TypeEntityMap[R]>;
          }
        : never
>;

export type SideloadedEntity<T extends keyof TypeEntityMap> = TypeEntityMap[T] & Reference<T>;

export type MaybeSideloadedEntity<T extends keyof TypeEntityMap> = SideloadedEntity<T> | Reference<T>;

export type IncludedEntities<T extends keyof TypeEntityMap> = Record<T, Array<TypeEntityMap[T]> | undefined>;

export type DurationUnit = 'min' | 'hour' | 'seconds' | 'minutes' | 's' | 'm';

export interface Duration<Unit extends DurationUnit> {
  unit: Unit;
  value: number;
}

export type CurrencyList = {
  code: string;
  symbol: string;
  name: string;
  exchangeRate: string;
}[];

export type Currency = string;

export interface Discount {
  description?: string;
  type: 'percentage';
  value: number;
}

export interface Money {
  amount: number;
  currency: Currency;
}

export interface CustomField {
  definition: Reference<Entity.CustomFieldDefinition>;
  value: TSFixMe | null;
}

export interface CustomFieldValue {
  id: string;
  value:
    | string
    | string[]
    | number
    | boolean
    | Reference<Entity.Company>
    | Reference<Entity.Contact>
    | Reference<Entity.Product>
    | Reference<Entity.User>
    | null;
}

export interface DateRange {
  before?: string;
  after?: string;
}
